import { useIsMobile } from '_hooks/useIsMobile';
import React, { useEffect, useState } from 'react';

interface DateOfBirthInputProps {
  value: string; // Expected in YYYY-MM-DD format
  onChange: (value: string) => void; // Returns YYYY-MM-DD format
  className?: string;
  min?: string; // YYYY-MM-DD format
  max?: string; // YYYY-MM-DD format
  required?: boolean;
  placeholder?: string;
}

/**
 * DateOfBirthInput component
 * On desktop: Shows native date picker
 * On mobile: Shows text input with MM/DD/YYYY format
 */
export const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
  value,
  onChange,
  className = '',
  min,
  max,
  required = false,
  placeholder = 'MM/DD/YYYY',
}) => {
  const isMobile = useIsMobile();
  const [displayValue, setDisplayValue] = useState('');
  const [rawInput, setRawInput] = useState(''); // Track actual digits entered (without padding)

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const convertToDisplayFormat = (dateStr: string): string => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Update display value when value prop changes from parent
  useEffect(() => {
    if (isMobile && value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        // Set raw input from the value prop
        setRawInput(`${parts[1]}${parts[2]}${parts[0]}`);
        setDisplayValue(convertToDisplayFormat(value));
      }
    } else if (isMobile && !value) {
      setRawInput('');
      setDisplayValue('');
    }
  }, [value, isMobile]);

  // Helper function to determine if a year is a leap year
  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  };

  // Helper function to get the number of days in a given month
  const getDaysInMonth = (month: number, year: number): number => {
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (month === 2 && isLeapYear(year)) {
      return 29;
    }

    return daysInMonth[month - 1] || 0;
  };

  const validateAndSetDate = (input: string) => {
    if (input.length === 8) {
      // Full 4-digit year entered (MMDDYYYY)
      const month = input.slice(0, 2);
      const day = input.slice(2, 4);
      const year = input.slice(4, 8);

      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);

      // Validate month range
      if (monthNum < 1 || monthNum > 12 || yearNum < 1900) {
        onChange('');
        return;
      }

      // Validate day range for the specific month
      const maxDays = getDaysInMonth(monthNum, yearNum);
      if (dayNum < 1 || dayNum > maxDays) {
        onChange('');
        return;
      }

      // Keep out-of-range values so the parent can show an error
      const standardFormat = `${year}-${month}-${day}`;
      onChange(standardFormat);
      return;
    } else if (input.length === 6) {
      // 2-digit year entered (MMDDYY) - auto-complete intelligently
      const month = input.slice(0, 2);
      const day = input.slice(2, 4);
      const yearShort = input.slice(4, 6);
      const yearShortNum = parseInt(yearShort, 10);

      // If year is less than 20, assume 2000s (e.g., 15 → 2015)
      // Otherwise assume 1900s (e.g., 88 → 1988)
      const year = yearShortNum < 20 ? `20${yearShort}` : `19${yearShort}`;

      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);

      // Validate month range
      if (monthNum < 1 || monthNum > 12 || yearNum < 1900) {
        onChange('');
        return;
      }

      // Validate day range for the specific month
      const maxDays = getDaysInMonth(monthNum, yearNum);
      if (dayNum < 1 || dayNum > maxDays) {
        onChange('');
        return;
      }

      const standardFormat = `${year}-${month}-${day}`;
      onChange(standardFormat);
      return;
    }

    // Clear if incomplete or invalid
    onChange('');
  };

  const handleMobileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ''); // Remove non-digits

    // Limit to 8 digits (MMDDYYYY)
    if (input.length > 8) {
      input = input.slice(0, 8);
    }

    setRawInput(input);

    // Format as MM/DD/YYYY
    let formatted = '';

    if (input.length > 0) {
      formatted = input.slice(0, 2);
      if (input.length >= 3) {
        formatted += `/${input.slice(2, 4)}`;
      }
      if (input.length >= 5) {
        const yearPart = input.slice(4);
        formatted += `/${yearPart}`;
      }
    }

    setDisplayValue(formatted);

    // Only validate when exactly 8 digits are entered
    if (input.length === 8) {
      validateAndSetDate(input);
    } else {
      // Clear the value if incomplete during typing
      onChange('');
    }
  };

  const handleMobileBlur = () => {
    // On blur, validate with current input (supports 6 or 8 digit entry)
    if (rawInput.length === 6 || rawInput.length === 8) {
      validateAndSetDate(rawInput);
    }
  };

  const handleDesktopChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Pass through values (including out-of-range) so the parent can show errors
    onChange(e.target.value);
  };

  if (isMobile) {
    return (
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleMobileInput}
        onBlur={handleMobileBlur}
        className={className}
        placeholder={placeholder}
        required={required}
        maxLength={10} // MM/DD/YYYY = 10 characters
      />
    );
  }

  return (
    <input
      type="date"
      value={value}
      onChange={handleDesktopChange}
      className={className}
      min={min}
      max={max}
      required={required}
    />
  );
};
